import { flattenObject } from "@rootreeweb/js-lib";
import { isUnionField } from "../typeGuards.js";
import { getArrayItemPath } from "./arrayFieldHelpers.js";
import { shouldShow } from "./helpers.js";
import { updateStateFromPrevious } from "./stateUpdating.js";
/**
 * Returns the element to use for the given field.
 *
 * @template T The type of the field to get the element for.
 * @param field The field to get the element for.
 * @param state The current state of the form.
 * @param controlLookup The lookup of controls to use for each field type.
 * @returns The element to use for the given field.
 * @throws Error An error if no control is found for the field.
 */
function getElement(field, state, controlLookup) {
    const matches = controlLookup[field.definition.typeName];
    if (!matches)
        throw new Error(`No control found for ${field.field}`);
    if (Array.isArray(matches)) {
        return (matches.find((m) => {
            return !m.test || m.test(field, state);
        }) || matches[0]);
    }
    return matches;
}
/**
 * Returns the element to use and a ready to use props object for the given
 * field.
 *
 * @template T The form shape.
 * @template S The component type.
 * @param options The options for the element.
 * @returns Null if the element should be hidden or the element to use and a
 *   ready to use props object for the given field.
 */
export function getElementAndProps(options) {
    if (!shouldShow(options.meta.field, options.formState, options.fields)) {
        return null;
    }
    const Element = getElement(options.meta.field, options.formState, options.controlLookup);
    const { element, event, props: extraProps, transform } = Element;
    const field = flattenObject({ ...options.meta.field, parent: null }); // The parent field will create a circular reference.
    if (options.arrayIndex !== undefined) {
        field.path = getArrayItemPath(options.meta.field, options.arrayIndex);
    }
    const eventHandler = {
        [event]: (e) => {
            let value;
            if (typeof e === "string") {
                value = e;
            }
            else {
                const { currentTarget } = e;
                const { value: targetValue, type: inputType } = currentTarget;
                if (inputType === "checkbox") {
                    if ("checked" in currentTarget) {
                        value = currentTarget.checked;
                    }
                    else {
                        throw new Error("Checkbox input must have a checked property when updating");
                    }
                }
                else {
                    value = targetValue;
                }
            }
            updateStateFromPrevious(options.setFormState, field.path, transform ? transform(String(value)) : value);
        },
    };
    if (isUnionField(field)) {
        field.options = field.options.filter((option) => shouldShow(option, options.formState, options.fields));
    }
    const combinedProps = {
        ...options.meta,
        field,
        ...(extraProps?.(field, options.formState) || {}),
        ...eventHandler,
    };
    return {
        element: element,
        combinedProps,
    };
}
/**
 * Adds the given control to the lookup.
 *
 * @template T The variant of ParsedField being used.
 * @param controlLookup The lookup to add the control to.
 * @param baseType The base type of the control. (ex. "ZodString").
 * @param control The control to add.
 */
export function addControl(controlLookup, baseType, control) {
    const existing = controlLookup[baseType];
    if (!existing) {
        controlLookup[baseType] = control;
        return;
    }
    if (Array.isArray(existing)) {
        controlLookup[baseType] = [control, ...existing];
        return;
    }
    controlLookup[baseType] = [control, existing];
}
//# sourceMappingURL=controlElementHelper.js.map